import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: globalThis.storeVariables?.eCommUrl ?? '/',
  prepareHeaders: (headers) => {
    // By default, if we have a token in the cookies, let's use that for authenticated requests
    const token = Cookies.get('esri_auth');
    headers.set('Authorization', token || 'anonymous');
    return headers;
  },
});

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const baseApi = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'api',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseQuery,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints.
   *
   * 'Cart' and 'Locale' are used by endpoints injected in adjacent files,
   * such as cart-api.js and locale-api.js, which provide tags for cache invalidation.
   * Ensure these tag names match the 'providesTags' or 'invalidatesTags' values in those endpoints.
   */
  tagTypes: ['Cart', 'Locale'],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
  /**
   * `refetchOnFocus` is optional, but useful for many applications.
   * This is useful to ensure data is up-to-date when the user focuses back on the tab,
   * e.g. after switching to a different tab in the browser.
   * Otherwise, data would only be updated when components mount or specific invalidation occurs
   */
  refetchOnFocus: false,
});
